<script lang="ts">
  import { Chip, type FacetValue } from "@otto-ec/heureka-components/facet-value";
  import { type FacetValueLabelOptions, Text } from "@otto-ec/heureka-components/facet-value-label";
  import DeselectAll from "../facetvaluetags/DeselectAll.svelte";
  import type { DeselectAllConfiguration } from "./type";
  import { OverflowBox } from "@otto-ec/heureka-components/facet-value-group";
  import { removeStatus } from "@otto-ec/heureka-components/tracking";
  import { SizePreferencesCookieStore } from "@otto-ec/heureka-components/facet";
  import { SvelteFacetValue } from "../filter/SvelteFacetValue";
  import { heureka } from "@otto-ec/heureka";
  import { sizePreferencesUpdateTrigger } from "./sizePreferencesStore";
  import { isActiveVariation } from "../experiment/Experiments";

  const defaultFacetValueLabelOptions: Partial<FacetValueLabelOptions> = {
    marginLeft: false,
    size: "75",
    showCount: false,
  };

  interface Props {
    nbaTitles?: string[];
    deselectAll: DeselectAllConfiguration;
    containsOnlyNbaValues?: boolean;
    values?: FacetValue[];
    hideDeselectAllOnDesktopView?: boolean;
  }

  const { nbaTitles, deselectAll, containsOnlyNbaValues, values, hideDeselectAllOnDesktopView }: Props = $props();
  /*                                              */
  const uniqueNbaTitles = nbaTitles ? Array.from(new Set(nbaTitles)) : undefined;
  const topQuickFacet = uniqueNbaTitles?.join("|") || "none";
  const hasValues = values?.length > 0;
  /*                                                                             */
  const nbaValues: FacetValue[] = values?.map(removeStatus) ?? [];

  /*                                                                           */
  /*                                  */
  /*                                                                           */

  const SIZE_CONFIGS = [
    { label: "EU", facetId: "shoesizeeuv2", prefsKey: "shoeSizeEUV2" },
    { label: "UK", facetId: "shoesizeukv2", prefsKey: "shoeSizeUKV2" },
    { label: "US", facetId: "shoesizeusv2", prefsKey: "shoeSizeUSV2" },
  ] as const;

  function nbaFromSvelteFacetValue(svelteFacetValue: SvelteFacetValue, label: string): FacetValue {
    return {
      id: `${svelteFacetValue.id}--nba`,
      name: svelteFacetValue.name,
      value: svelteFacetValue.title.replace(",", "-"),
      title: `${label} ${svelteFacetValue.title}`,
      checked: svelteFacetValue.initiallyChecked,
      popular: svelteFacetValue.popular,
      nba: svelteFacetValue.nba,
      referencedFacetValueId: `${svelteFacetValue.id}`,
      featureTracking: { labels: { san_FilterMethod: ["quick_filter"], san_FacetLocation: ["list_initial"] } },
    };
  }

  function findOrCreateFacetValue(
    label: string,
    facetId: string,
    size: string,
    existingTitles?: Set<string>,
  ): FacetValue | undefined {
    const title = `${label} ${size}`;

    /*                                     */
    if (existingTitles?.has(title)) return undefined;

    /*                                  */
    const matchedNba = nbaValues.find((nba) => nba.title === title);
    if (matchedNba) return matchedNba;

    /*                                         */
    const svelteFacetValue = SvelteFacetValue.factory.pick(`#facetValue_${facetId}_${size.replace(",", "-")}`);
    if (svelteFacetValue) {
      return nbaFromSvelteFacetValue(svelteFacetValue, label);
    }

    return undefined;
  }

  /*                                                                           */
  /*                  */
  /*                                                                           */

  /*                      */
  const saveSizeValues = $derived.by(() => {
    if (!isActiveVariation("e3663", "E3663B")) return [];

    void $sizePreferencesUpdateTrigger;

    const preferences = SizePreferencesCookieStore.get();
    if (!preferences) return [];

    const result: FacetValue[] = [];

    for (const { label, facetId, prefsKey } of SIZE_CONFIGS) {
      const sizes = preferences[prefsKey] || [];
      for (const size of sizes) {
        const facetValue = findOrCreateFacetValue(label, facetId, size);
        if (facetValue) result.push(facetValue);
      }
    }

    return result;
  });

  /*                                                                                                 */
  const initiallyCheckedSizeValuesNotInCookie = $derived.by(() => {
    if (!isActiveVariation("e3663", "E3663B")) return [];

    void $sizePreferencesUpdateTrigger;

    const excludeTitles = new Set([...saveSizeValues.map((v) => v.title), ...nbaValues.map((v) => v.title)]);
    const result: FacetValue[] = [];

    for (const { label, facetId } of SIZE_CONFIGS) {
      const facetElement = document.getElementById(`facet_${facetId}`);
      if (!facetElement) continue;

      const checkedInputs = facetElement.querySelectorAll<HTMLInputElement>('input[type="checkbox"]:checked');
      for (const input of checkedInputs) {
        const svelteFacetValue = new SvelteFacetValue(input);
        if (!svelteFacetValue?.title || !svelteFacetValue.initiallyChecked) continue;

        const title = `${label} ${svelteFacetValue.title}`;
        if (excludeTitles.has(title)) continue;

        const facetValue = nbaFromSvelteFacetValue(svelteFacetValue, label);
        facetValue.checked = true;
        result.push(facetValue);
      }
    }

    return result;
  });

  /*                                                                        */
  function wasInitiallyChecked(value: FacetValue): boolean {
    if (value.referencedFacetValueId) {
      const svelteFacetValue = SvelteFacetValue.factory.pick(`#${value.referencedFacetValueId}`);
      return svelteFacetValue?.initiallyChecked ?? value.checked;
    }
    return value.checked;
  }

  /*                                                             */
  /*                                                                                                      */
  const selectedSavedSizes = $derived(saveSizeValues?.filter((value) => wasInitiallyChecked(value)) ?? []);
  const unselectedSavedSizes = $derived(saveSizeValues?.filter((value) => !wasInitiallyChecked(value)) ?? []);

  /*                                      */
  const savedSizeTitles = $derived(new Set(saveSizeValues.map((v) => v?.title)));

  /*                                                                                                       */
  const selectedValues = $derived([
    ...nbaValues.filter((value) => !savedSizeTitles.has(value.title)).filter((value) => value.checked),
    ...initiallyCheckedSizeValuesNotInCookie,
  ]);
  const unselectedValues =
    nbaValues.filter((value) => !savedSizeTitles.has(value.title)).filter((value) => !value.checked) ?? [];

  const hasSelectedValues = $derived(nbaValues.find((value: FacetValue) => value.checked));

  $effect(() => {
    void unselectedSavedSizes;
    void selectedSavedSizes;
    heureka.refinementBar_update.emit();
  });
</script>

{#snippet closeIcon()}
  <oc-icon-v1 class="hc_facetValue__icon" type="close" size="50"></oc-icon-v1>
{/snippet}

{#snippet heartIcon()}
  <oc-icon-v1 class="hc_facetValue__icon" type="wishlist-active" size="50"></oc-icon-v1>
{/snippet}

{#snippet chip(facetValue: FacetValue)}
  <Chip class={[{ js_clearInput: facetValue.checked }]} {...facetValue}>
    <Text {...facetValue} {...defaultFacetValueLabelOptions} />
    {#if facetValue.checked}
      {@render closeIcon()}
    {/if}
  </Chip>
{/snippet}

{#snippet heartChip(facetValue: FacetValue)}
  <Chip class={[{ js_clearInput: facetValue.checked }]} {...facetValue}>
    {@render heartIcon()}
    <Text {...facetValue} {...defaultFacetValueLabelOptions} marginLeft={true} />
    {#if facetValue.checked}
      {@render closeIcon()}
    {/if}
  </Chip>
{/snippet}

<div
  class="hc_nextBestAction{hasSelectedValues ? '' : ' hc_nextBestAction--unselected'}"
  hidden={!hasValues}
  data-ts-top-quick-facet={topQuickFacet}
  data-ts-nba-facet-value-count={nbaValues.filter((value: FacetValue) => value.nba).length}
  data-ts-labels={JSON.stringify({
    san_NBAFacetValueCount: nbaValues.filter((value: FacetValue) => value.nba).length,
  })}
>
  {#if hasValues}
    <h4>
      {#if containsOnlyNbaValues && !(selectedValues.length > 0)}Beliebte Filter{:else}Gewählte und beliebte Filter{/if}
    </h4>
    <div class="hc_nextBestAction__content">
      <div class="hc_nextBestAction__values">
        <OverflowBox values={selectedValues} item={chip}></OverflowBox>
        <OverflowBox values={selectedSavedSizes} item={heartChip}></OverflowBox>
        <OverflowBox values={unselectedSavedSizes} item={heartChip}></OverflowBox>
        <OverflowBox values={unselectedValues} item={chip}></OverflowBox>
      </div>
      {#if hasSelectedValues}
        <div
          class="hc_nextBestAction__deselectAll"
          class:hc_facetValueTags__deselectAll--hide-desktop={hideDeselectAllOnDesktopView}
        >
          <DeselectAll {...deselectAll} />
        </div>
      {/if}
    </div>
  {/if}
</div>

<style lang="scss">
  @use "@otto-ec/design-tokens/tokens";

  .hc_nextBestAction {
    margin-bottom: tokens.$oc-semantic-spacing-50;
    &--unselected {
      padding-bottom: tokens.$oc-semantic-spacing-50;
    }

    h4 {
      padding: tokens.$oc-semantic-spacing-50 0 tokens.$oc-semantic-spacing-50;
      font: tokens.$oc-semantic-font-copy-75;
      color: tokens.$oc-semantic-color-text-secondary;
    }

    &__content {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 14px 0;
    }

    &__values {
      display: flex;
      /*              */
      gap: tokens.$oc-semantic-spacing-50;
      flex: 1 1 auto;
    }

    &__values {
      overflow-x: auto;
      overflow-y: hidden;
      scrollbar-width: none;

      .hc_facetValue__icon {
        width: tokens.$oc-semantic-spacing-75;
        height: tokens.$oc-semantic-spacing-75;
        margin-left: tokens.$oc-semantic-spacing-25;
      }
    }

    &__deselectAll {
      flex: 1;
      white-space: nowrap;
      text-align: right;
      padding-left: tokens.$oc-semantic-spacing-75;
      padding-right: tokens.$oc-semantic-spacing-25;
    }
  }
</style>
